import { Fragment, useCallback, useMemo, useState, CSSProperties } from 'react';
import Dropzone from 'react-dropzone';
import { Col, FormText, Row } from 'reactstrap';
import { Button, FileInputProps, Icon } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { uploadFile } from './api';
import { FileRequiredTypes, SecureResponseFile } from './types';
import { AxiosResultDefaultError } from '../../api/request';

const SUPPORTED_FILES = {
  images: 'image/jpg, image/jpeg, image/png',
  documents:
    'application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  spreadsheets:
    'application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
} as const;

type SupportedFile = keyof typeof SUPPORTED_FILES;

export interface AzureFilesUploaderProps
  extends Omit<
    FileInputProps,
    'name' | 'onDownloadFile' | 'onChange' | 'onDrop' | 'value' | 'accept'
  > {
  className?: string;
  style?: CSSProperties;
  name?: string;
  label?: string;
  disabled?: boolean;
  error?: string;
  btnText?: string;
  fileType?: FileRequiredTypes;
  accept?: SupportedFile[];
  onTrash?: () => void;
  onUploaded?: (file: SecureResponseFile) => void;
  onError?: (e?: AxiosResultDefaultError | false) => void;
  value?: Pick<SecureResponseFile, 'name'> | null;
}

const AzureFilesUploader = ({
  className,
  style,
  btnText,
  disabled = false,
  name = 'file',
  label,
  onTrash,
  onUploaded,
  onError,
  error,
  fileType,
  accept = Object.keys(SUPPORTED_FILES) as SupportedFile[],
  value,
  ...dropzoneProps
}: AzureFilesUploaderProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);

  const acceptDropzone = useMemo(() => {
    if (!accept?.length) {
      return undefined;
    }
    const accepteds = new Set<string>(accept.map((t) => SUPPORTED_FILES[t]));
    return Array.from(accepteds).join(', ');
  }, [accept]);

  const handleDropFiles = useCallback(
    async (file: File) => {
      if (loading) {
        return;
      }
      setLoading(true);
      const { data, error } = await uploadFile(file, fileType);
      setLoading(false);

      if (error) {
        !!onError && onError(error as any);
      } else if (data?.id) {
        !!onUploaded && onUploaded(data);
      } else {
        !!onError && onError();
      }
    },
    [loading, fileType, onError, onUploaded],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) {
        return;
      }
      handleDropFiles(acceptedFiles[0]);
    },
    [handleDropFiles],
  );

  return (
    <div className={className} style={style}>
      <label className="g-file-input-label text-muted" htmlFor={name}>
        {label || t('common:actions.addFiles')}
      </label>
      <Dropzone
        {...dropzoneProps}
        multiple={false}
        accept={acceptDropzone}
        onDrop={onDrop}
      >
        {({ getInputProps, open }) => (
          <Fragment>
            <Row>
              {value ? (
                <Col xs={12} sm={6} className="mb-2">
                  <div className="h-100 bg-light" style={{ padding: '8px' }}>
                    <Row className="h-100 align-items-center">
                      <Col xs="auto">
                        <Icon
                          name="file_img"
                          size="25px"
                          color={disabled ? 'inherit' : 'primary'}
                        />
                      </Col>
                      <Col xs>{value.name}</Col>
                      {Boolean(onTrash) && !disabled && (
                        <Col xs="auto">
                          <span role="button" onClick={onTrash}>
                            <Icon name="trash" size="16px" />
                          </span>
                        </Col>
                      )}
                    </Row>
                  </div>
                </Col>
              ) : (
                <></>
              )}
              <Col xs={12} sm className="mb-2">
                <Button
                  text={btnText || t('common:actions.uploadFiles')}
                  color="primary"
                  outlined
                  size="md"
                  onClick={open}
                  icon="export"
                  type="button"
                  className="w-100"
                  fullwidth
                  loading={loading}
                  disabled={disabled}
                />
              </Col>
            </Row>
            <input name={name} {...getInputProps()} disabled={disabled} />
            {Boolean(error) && (
              <FormText className="g-input-error" color="danger">
                {error}
              </FormText>
            )}
          </Fragment>
        )}
      </Dropzone>
    </div>
  );
};

export default AzureFilesUploader;
